import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Coffee } from 'lucide-react';
import type { ResumableTimer } from '../types';
import Dialog from './ui/Dialog';
import { useStartTimer } from './home/useHomeData';

export const BREAK_RESUME_KEY = ['break-resume-offer'] as const;

/** Called after POST /attendance/break-end: offer (never auto-start) the paused timers. */
export function offerBreakResume(
  queryClient: ReturnType<typeof useQueryClient>,
  timers: ResumableTimer[] | undefined
): void {
  if (timers && timers.length > 0) queryClient.setQueryData(BREAK_RESUME_KEY, timers);
}

/**
 * B.21 — returning from a break OFFERS to resume the timers the break paused.
 * Mounted once (inside GlobalTimer); fed via the react-query cache.
 *
 * P-10a (D-63): "המשך" starts each chosen task through the ONE start flow
 * (`useStartTimer`: the clock-in check, the "יש טיימר פעיל" question, the
 * planning → in-progress move), like every other entry point. The tasks
 * chosen here together never ask "stop and switch" about each other.
 */
export default function BreakResumeOffer() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useQuery<ResumableTimer[]>({
    queryKey: BREAK_RESUME_KEY,
    queryFn: async () => [],
    enabled: false,
    staleTime: Infinity,
  });
  const offered = data ?? [];
  const [unchecked, setUnchecked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const startTimer = useStartTimer({ silentErrors: true });

  if (offered.length === 0) return null;

  const close = () => {
    queryClient.setQueryData(BREAK_RESUME_KEY, []);
    setUnchecked([]);
    setError('');
  };

  const resume = async () => {
    setBusy(true);
    setError('');
    const failed: string[] = [];
    const chosen = offered.filter((o) => !unchecked.includes(o.ticket_id));
    const together = chosen.map((o) => o.ticket_id);
    for (const tm of chosen) {
      try {
        await startTimer.mutateAsync({ id: tm.ticket_id, title: tm.ticket_title, runAlongside: together });
      } catch (err: any) {
        failed.push(`${tm.ticket_title}: ${err?.error?.message || t('timer.startError')}`);
      }
    }
    setBusy(false);
    if (failed.length > 0) setError(failed.join(' · '));
    else close();
  };

  const selectedCount = offered.length - unchecked.length;

  return (
    <Dialog
      open
      onClose={close}
      title={t('timeTasks.breakResume.title')}
      description={t('timeTasks.breakResume.help')}
      icon={<Coffee size={18} className="text-success-700" aria-hidden="true" />}
      size="md"
      dismissible={!busy}
      footer={(
        <>
          <button type="button" onClick={close} className="btn btn-secondary" disabled={busy}>{t('timeTasks.breakResume.notNow')}</button>
          <button
            type="button"
            onClick={resume}
            disabled={busy || selectedCount === 0}
            aria-busy={busy || undefined}
            className="btn btn-primary"
          >
            {t('timeTasks.breakResume.resume')}
          </button>
        </>
      )}
    >
      <fieldset>
        <legend className="sr-only">{t('timeTasks.breakResume.title')}</legend>
        <ul className="space-y-1">
          {offered.map((tm) => (
            <li key={tm.ticket_id}>
              <label className="flex min-h-touch items-center gap-2 text-sm text-text sm:min-h-9">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!unchecked.includes(tm.ticket_id)}
                  onChange={(e) =>
                    setUnchecked((u) => (e.target.checked ? u.filter((x) => x !== tm.ticket_id) : [...u, tm.ticket_id]))
                  }
                />
                <span className="truncate">{tm.ticket_title}</span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      {error && <p role="alert" className="mt-3 rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</p>}
    </Dialog>
  );
}
